// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Alert, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import DetailsPanel from '../../Common/DetailsPanel';
import { RetryAlert } from '../../Common/ErrorMessages';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
import { LoadClient, useLoaded } from '../ScopingModels/useLoaded';
import GoldenDatasetRowsTable from './GoldenDatasetRowsTable';
import * as goldenDatasets from './goldenDatasets';

interface GoldenDatasetVersionProps {
  apiClient: LoadClient;
  datasetId: string;
  version: number;
}

/** A dataset as written at one version, read-only. */
export const GoldenDatasetVersion = ({
  apiClient,
  datasetId,
  version,
}: GoldenDatasetVersionProps): JSX.Element => {
  const snapshot = useLoaded<unknown>(
    apiClient,
    goldenDatasets.versionPath(datasetId, version),
    goldenDatasets.describeError
  );
  const dataset = snapshot.value && goldenDatasets.toGoldenDataset(snapshot.value);

  return (
    <SpaceBetween size="m">
      <Link id="goldenDatasetLink" to={goldenDatasets.datasetRoute(datasetId)}>
        Back to the live version
      </Link>
      {snapshot.missing && (
        <Alert id="goldenDatasetVersionMissing" type="error">
          {`Version ${version} of this golden dataset was not found.`}
        </Alert>
      )}
      {snapshot.errors.length > 0 && (
        <RetryAlert
          id="goldenDatasetVersionError"
          header="Could not load the version"
          messages={snapshot.errors}
          retryId="retryGoldenDatasetVersion"
          onRetry={snapshot.reload}
        />
      )}
      {!dataset && !snapshot.missing && snapshot.errors.length === 0 && (
        <span>
          <Spinner /> Loading the version
        </span>
      )}
      {dataset && (
        <SpaceBetween size="m">
          <Header
            variant="h2"
            description={`${goldenDatasets.CHANGE_LABELS[dataset.change]} ${convertDateTimePretty(
              dataset.updatedAt
            )} by ${dataset.author}`}
          >
            {`Version ${dataset.version} of ${dataset.name}`}
          </Header>
          <DetailsPanel
            items={[
              { label: 'Description', value: valueOrDash(dataset.description) },
              { label: 'Pricing model', value: dataset.pricingModelName },
              { label: 'Scoping model', value: dataset.scopingModelName },
            ]}
          />
          <GoldenDatasetRowsTable rows={dataset.rows} />
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

GoldenDatasetVersion.propTypes = {
  apiClient: PropTypes.object.isRequired,
  datasetId: PropTypes.string.isRequired,
  version: PropTypes.number.isRequired,
};

const GoldenDatasetVersionView = (
  props: Omit<GoldenDatasetVersionProps, 'apiClient'>
): JSX.Element => <GoldenDatasetVersion apiClient={ApiClient} {...props} />;

export default GoldenDatasetVersionView;
